import React, { useState } from "react";
import { signInWithEmailAndPassword } from "firebase/auth";
import { auth, db } from "./firebase";
import { doc, getDoc } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { 
  Activity,
  Mail, 
  Lock, 
  Eye, 
  EyeOff, 
  ArrowRight,
  AlertCircle,
  CheckCircle
  
} from "lucide-react";
import LogoMikDok from "./components/common/LogoMikDok"
const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  
  const navigate = useNavigate();

  // Modern styles object
  const styles = {
    container: {
      minHeight: "100vh",
      background: "linear-gradient(135deg, #f0f9ff 0%, #ffffff 30%, #f0fdf4 100%)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "16px",
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
    },
    loginCard: {
      backgroundColor: "rgba(255, 255, 255, 0.9)",
      backdropFilter: "blur(20px)",
      borderRadius: "24px",
      boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.15)",
      padding: "48px",
      width: "100%",
      maxWidth: "440px",
      border: "1px solid rgba(255, 255, 255, 0.2)"
    },
    header: {
      textAlign: "center",
      marginBottom: "32px"
    },
    logoContainer: {
      background: "linear-gradient(135deg, #3b82f6, #6366f1)",
      padding: "16px",
      borderRadius: "20px",
      display: "inline-block",
      marginBottom: "24px",
      boxShadow: "0 10px 25px -5px rgba(59, 130, 246, 0.3)"
    },
    title: {
      fontSize: "28px",
      fontWeight: "700",
      background: "linear-gradient(135deg, #1f2937, #6b7280)",
      WebkitBackgroundClip: "text",
      WebkitTextFillColor: "transparent",
      margin: "0 0 8px 0"
    },
    subtitle: {
      fontSize: "16px",
      color: "#6b7280",
      margin: 0
    },
    form: {
      display: "flex",
      flexDirection: "column",
      gap: "24px"
    },
    inputGroup: {
      position: "relative"
    },
    label: {
      display: "block",
      fontSize: "14px",
      fontWeight: "500",
      color: "#374151",
      marginBottom: "8px"
    },
    inputWrapper: {
      position: "relative",
      display: "flex",
      alignItems: "center"
    },
    input: {
      width: "100%",
      padding: "16px 16px 16px 48px",
      border: "2px solid #e5e7eb",
      borderRadius: "12px",
      fontSize: "16px",
      transition: "all 0.2s ease",
      backgroundColor: "#f9fafb",
      boxSizing: "border-box",
      outline: "none"
    },
    inputIcon: {
      position: "absolute",
      left: "16px",
      color: "#9ca3af",
      zIndex: 1
    },
    passwordToggle: {
      position: "absolute",
      right: "16px",
      background: "none",
      border: "none",
      color: "#9ca3af",
      cursor: "pointer",
      padding: "4px",
      borderRadius: "4px",
      transition: "color 0.2s ease"
    },
    button: {
      width: "100%",
      background: "linear-gradient(135deg, #3b82f6, #6366f1)",
      color: "white",
      padding: "16px",
      borderRadius: "12px",
      fontSize: "16px",
      fontWeight: "600",
      border: "none",
      cursor: "pointer",
      transition: "all 0.2s ease",
      boxShadow: "0 10px 25px -5px rgba(59, 130, 246, 0.3)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "8px"
    },
    buttonDisabled: {
      opacity: 0.5,
      cursor: "not-allowed"
    },
    spinner: {
      width: "20px",
      height: "20px",
      border: "2px solid transparent",
      borderTop: "2px solid white",
      borderRadius: "50%",
      animation: "spin 1s linear infinite"
    },
    alert: {
      padding: "16px",
      borderRadius: "12px",
      fontSize: "14px",
      fontWeight: "500",
      display: "flex",
      alignItems: "center",
      gap: "8px",
      marginBottom: "24px"
    },
    errorAlert: {
      backgroundColor: "#fee2e2",
      border: "1px solid #fecaca",
      color: "#dc2626"
    },
    successAlert: {
      backgroundColor: "#dcfce7",
      border: "1px solid #bbf7d0",
      color: "#16a34a"
    },
    footer: {
      textAlign: "center",
      marginTop: "32px",
      padding: "24px 0",
      borderTop: "1px solid #e5e7eb"
    },
    footerText: {
      fontSize: "14px",
      color: "#6b7280",
      margin: "0 0 16px 0"
    },
    linkButton: {
      background: "none",
      border: "none",
      color: "#3b82f6",
      fontSize: "14px",
      fontWeight: "500",
      cursor: "pointer",
      textDecoration: "underline",
      transition: "color 0.2s ease"
    },
    features: {
      display: "grid",
      gridTemplateColumns: "repeat(3, 1fr)",
      gap: "16px",
      marginTop: "24px"
    },
    feature: {
      textAlign: "center",
      padding: "16px",
      backgroundColor: "rgba(248, 250, 252, 0.8)",
      borderRadius: "12px",
      border: "1px solid rgba(229, 231, 235, 0.5)"
    },
    featureIcon: {
      width: "32px",
      height: "32px",
      margin: "0 auto 8px auto",
      padding: "8px",
      backgroundColor: "#dbeafe",
      borderRadius: "8px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center"
    },
    featureText: {
      fontSize: "12px",
      fontWeight: "500",
      color: "#374151",
      margin: 0
    }
  };

  const handleLogin = async () => {
    if (!email || !password) {
      setError("Please fill in all fields");
      return;
    }

    setLoading(true);
    setError("");
    setSuccess("");

    try {
      const userCred = await signInWithEmailAndPassword(auth, email, password);
      const uid = userCred.user.uid;

      // Get user role from Firestore
      const userDoc = await getDoc(doc(db, "users", uid));
      
      if (!userDoc.exists()) {
        throw new Error("User profile not found. Please contact support.");
      }

      const userData = userDoc.data();
      const role = userData.role;

      setSuccess("Login successful! Redirecting...");

      // Navigate based on role
     setTimeout(() => {
  const isProfileComplete = userData.profileComplete ?? false;

  if (role === "doctor") {
    if (!isProfileComplete) {
      navigate("/doctor-profile-quick");
    } else {
      navigate("/dashboard-doctor");
    }

  } else if (role === "patient") {
    if (!isProfileComplete) {
      navigate("/patient-setup");
    } else {
      navigate("/dashboard-patient");
    }

  } else if (role === "clinic") {
    if (!isProfileComplete) {
      navigate("/clinic-profile-setup");
    } else {
      navigate("/dashboard-clinic");
    }

  } else if (role === "admin") {
    navigate("/admin-dashboard");

  } else {
    throw new Error("Invalid user role. Please contact support.");
  }
}, 1000);


    } catch (err) {
      console.error("Login error:", err);
      
      // User-friendly error messages
      let errorMessage = "Login failed. Please try again.";
      
      if (err.code === "auth/user-not-found") {
        errorMessage = "No account found with this email address.";
      } else if (err.code === "auth/wrong-password") {
        errorMessage = "Incorrect password. Please try again.";
      } else if (err.code === "auth/invalid-email") {
        errorMessage = "Please enter a valid email address.";
      } else if (err.code === "auth/user-disabled") {
        errorMessage = "This account has been disabled. Please contact support.";
      } else if (err.code === "auth/too-many-requests") {
        errorMessage = "Too many failed attempts. Please try again later.";
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleInputFocus = (e) => {
    e.target.style.borderColor = "#3b82f6";
    e.target.style.backgroundColor = "white";
    e.target.style.boxShadow = "0 0 0 3px rgba(59, 130, 246, 0.1)";
  };

  const handleInputBlur = (e) => {
    e.target.style.borderColor = "#e5e7eb";
    e.target.style.backgroundColor = "#f9fafb";
    e.target.style.boxShadow = "none";
  };

  return (
    <div style={styles.container}>
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        .login-button:hover:not(:disabled) {
          background: linear-gradient(135deg, #1d4ed8, #4338ca) !important;
          transform: translateY(-2px);
          box-shadow: 0 20px 25px -5px rgba(59, 130, 246, 0.4) !important;
        }
        
        .password-toggle:hover {
          color: #374151 !important;
          background-color: #f3f4f6;
        }
        
        .link-button:hover {
          color: #1d4ed8 !important;
        }
        
        .feature-card:hover {
          background-color: rgba(239, 246, 255, 0.8) !important;
          transform: translateY(-2px);
          transition: all 0.2s ease;
        }
        
        @media (max-width: 640px) {
          .login-card {
            padding: 32px 24px !important;
            margin: 16px !important;
          }
          
          .features-grid {
            grid-template-columns: 1fr !important;
            gap: 12px !important;
          }
        }
      `}</style>
      
      <div style={styles.loginCard} className="login-card">
        {/* Header */}
        <div style={styles.header}>
        <LogoMikDok
    subtitle="Login"
    badge={50}
    size={34}
    markColor="#ffffff"
  />

          <h1 style={styles.title}>Welcome to MikDok</h1>
          <p style={styles.subtitle}>Sign in to access your medical dashboard</p>
        </div>

        {/* Alert Messages */}
        {error && (
          <div style={{...styles.alert, ...styles.errorAlert}}>
            <AlertCircle style={{ width: "16px", height: "16px" }} />
            {error}
          </div>
        )}

        {success && (
          <div style={{...styles.alert, ...styles.successAlert}}>
            <CheckCircle style={{ width: "16px", height: "16px" }} />
            {success}
          </div>
        )}

        {/* Login Form */}
        <div style={styles.form}>
          <div style={styles.inputGroup}>
            <label style={styles.label} htmlFor="email">Email Address</label>
            <div style={styles.inputWrapper}>
              <Mail style={{...styles.inputIcon, width: "20px", height: "20px"}} />
              <input
                id="email"
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onFocus={handleInputFocus}
                onBlur={handleInputBlur}
                style={styles.input}
                disabled={loading}
                required
              />
            </div>
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.label} htmlFor="password">Password</label>
            <div style={styles.inputWrapper}>
              <Lock style={{...styles.inputIcon, width: "20px", height: "20px"}} />
              <input
                id="password"
                type={showPassword ? "text" : "password"}
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onFocus={handleInputFocus}
                onBlur={handleInputBlur}
                style={styles.input}
                disabled={loading}
                required
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleLogin();
                  }
                }}
              />
              <button
                type="button"
                style={styles.passwordToggle}
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
                disabled={loading}
              >
                {showPassword ? 
                  <EyeOff style={{ width: "20px", height: "20px" }} /> : 
                  <Eye style={{ width: "20px", height: "20px" }} />
                }
              </button>
            </div>
          </div>

          <button
            type="button"
            onClick={handleLogin}
            style={{
              ...styles.button,
              ...(loading ? styles.buttonDisabled : {})
            }}
            className="login-button"
            disabled={loading}
          >
            {loading ? (
              <>
                <div style={styles.spinner}></div>
                Signing in...
              </>
            ) : (
              <>
                Sign In
                <ArrowRight style={{ width: "20px", height: "20px" }} />
              </>
            )}
          </button>
        </div>

        {/* Footer */}
        <div style={styles.footer}>
          <p style={styles.footerText}>Don't have an account?</p>
          <button 
            style={styles.linkButton}
            className="link-button"
            onClick={() => navigate("/register")}
          >
            Create Account
          </button>
          <span style={{ color: "#d1d5db", margin: "0 8px" }}>•</span>
          <button 
            style={styles.linkButton}
            className="link-button"
            onClick={() => navigate("/forgot-password")}
          >
            Forgot Password?
          </button>
        </div>

        {/* Features */}
        <div style={styles.features} className="features-grid">
          <div style={styles.feature} className="feature-card">
            <div style={styles.featureIcon}>
              <Activity style={{ width: "16px", height: "16px", color: "#3b82f6" }} />
            </div>
            <p style={styles.featureText}>Secure Platform</p>
          </div>
          
          <div style={styles.feature} className="feature-card">
            <div style={styles.featureIcon}>
              <Mail style={{ width: "16px", height: "16px", color: "#3b82f6" }} />
            </div>
            <p style={styles.featureText}>Easy Communication</p>
          </div>
          
          <div style={styles.feature} className="feature-card">
            <div style={styles.featureIcon}>
              <CheckCircle style={{ width: "16px", height: "16px", color: "#3b82f6" }} />
            </div>
            <p style={styles.featureText}>Verified Doctors</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;